import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Search, MessageCircle, Eye } from "lucide-react";

/**
 * AdminInterpretations — the artist's window into "what people saw" before
 * the artwork's official story unlocked. Filterable by artwork, deletable per row.
 */
export default function AdminInterpretations() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [artworks, setArtworks] = useState([]);
  const [artworkId, setArtworkId] = useState("");
  const [q, setQ] = useState("");

  const load = async (filterId = "") => {
    try {
      const params = filterId ? { artwork_id: filterId } : {};
      const [{ data }, { data: arts }] = await Promise.all([
        http.get("/admin/interpretations", { params }),
        http.get("/artworks"),
      ]);
      setRows(data.items || []);
      setCounts(data.counts_by_artwork || {});
      setArtworks(arts || []);
    } catch { toast.error("Could not load interpretations"); }
  };
  useEffect(() => { load(artworkId); }, [artworkId]);

  const remove = async (id) => {
    if (!window.confirm("Delete this interpretation?")) return;
    try { await http.delete(`/admin/interpretations/${id}`); toast.success("Deleted"); load(artworkId); }
    catch { toast.error("Failed"); }
  };

  const filtered = q
    ? rows.filter((r) => (r.text || "").toLowerCase().includes(q.toLowerCase()) || (r.artwork_title || "").toLowerCase().includes(q.toLowerCase()) || (r.name || "").toLowerCase().includes(q.toLowerCase()))
    : rows;

  return (
    <div className="space-y-5" data-testid="admin-interpretations">
      <div>
        <div className="font-display text-2xl text-[#F4F1EA]">"What Do You See?" — Interpretations</div>
        <p className="font-body text-[13px] text-[#8a857d] mt-1">
          What people felt when they first saw the artwork — before reading the meaning. {Object.values(counts).reduce((a, b) => a + b, 0)} responses across {Object.keys(counts).length} artworks.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <label className="block">
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase mb-1">Filter by artwork</div>
          <select value={artworkId} onChange={(e) => setArtworkId(e.target.value)} className="bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[13px] focus:border-[#C9A055] focus:outline-none min-w-[260px]" data-testid="interp-artwork-filter">
            <option value="">All artworks ({rows.length})</option>
            {artworks.map((a) => (
              <option key={a.id} value={a.id}>{a.title} {counts[a.id] ? `· ${counts[a.id]}` : ""}</option>
            ))}
          </select>
        </label>
        <label className="block flex-1 min-w-[200px]">
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase mb-1">Search</div>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a857d]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Text, name, or artwork…" className="w-full bg-black border border-white/15 pl-9 pr-3 py-2 text-[#F4F1EA] font-body text-[13px] focus:border-[#C9A055] focus:outline-none" data-testid="interp-search"/>
          </div>
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-white/10 bg-[#0a0a0a] p-12 text-center font-body italic text-[#8a857d]">
          <MessageCircle size={28} className="mx-auto mb-3 text-[#5a5650]"/>
          No interpretations yet{artworkId ? " for this artwork" : ""}.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.id} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`interp-${r.id}`}>
              <div className="flex justify-between items-start gap-3 flex-wrap mb-2">
                <div>
                  <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">{r.artwork_title || "Untitled"}</div>
                  <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase mt-1">
                    {r.name || "Anonymous"} {r.email && `· ${r.email}`} · {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={() => remove(r.id)} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#d97a7a]" data-testid={`interp-delete-${r.id}`}><Trash2 size={13}/></button>
              </div>
              <p className="font-body text-[14px] text-[#F4F1EA] whitespace-pre-line leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
